// Renders menu HTML and interactive links inside an iframe.
//
// First-party menu render URLs get same-origin sandbox access so existing menu
// scripts and forms continue to work. External links are sandboxed more tightly
// and never get the QR fallback; if they cannot be framed, the item is reported
// as a content error and the carousel advances.

import { useEffect, useRef, useState } from 'react'
import { absoluteUrl } from '../lib/contentUrl'
import ContentErrorScreen from './ContentErrorScreen'

interface Props {
  renderUrl: string
  linkUrl?: string | null
  itemName?: string
  durationMs: number
  onFinished: () => void
  onError: (msg: string) => void
}

const warmed = new Set<string>()
const framingBlocked = new Set<string>()
// How long to wait for an external page before giving up on it.
//
// Was a flat 4s, which is the bug behind a constant stream of "This content
// could not be loaded" on screens that were displaying the page perfectly
// well. A timeout cannot tell REFUSED from SLOW: a heavy third-party page, a
// cold CDN or a venue on poor wifi routinely takes longer than four seconds to
// fire onLoad, and every one of those was recorded as a framing refusal —
// reported to Sentry AND the device log on every rotation, so a working screen
// generated errors forever.
//
// The floor is generous and the window also scales with the item's own dwell:
// if the operator gave a page 30 seconds on screen, it has not failed at four.
const FRAME_LOAD_FLOOR_MS = 20_000

export default function HtmlFrameDisplay({
  renderUrl, linkUrl, durationMs, onFinished, onError,
}: Props) {
  const fullUrl = absoluteUrl(renderUrl)
  const wasWarmed = warmed.has(fullUrl)
  const [loaded, setLoaded] = useState(wasWarmed)
  const [hasError, setHasError] = useState(() => !!linkUrl && framingBlocked.has(fullUrl))
  const finishedRef = useRef(false)

  const advance = () => {
    if (finishedRef.current) return
    finishedRef.current = true
    onFinished()
  }

  // `sticky` marks the URL as blocked for the rest of the session, so later
  // rotations skip straight to the error instead of re-waiting. Only a REAL
  // refusal earns that. A timeout must not: it cannot distinguish refused from
  // slow, and poisoning the set on one bad network moment made every later
  // showing of a perfectly good page fail instantly and report again.
  const fail = (msg: string, sticky = false) => {
    if (sticky) framingBlocked.add(fullUrl)
    setHasError(true)
    setLoaded(true)
    onError(msg)
  }

  useEffect(() => {
    if (!linkUrl || loaded || hasError) return
    const budget = Math.max(FRAME_LOAD_FLOOR_MS, durationMs)
    const t = window.setTimeout(() => {
      if (!warmed.has(fullUrl)) {
        fail(`External link did not load within ${Math.round(budget / 1000)}s: ${linkUrl}`)
      }
    }, budget)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl, linkUrl, loaded, hasError, durationMs])

  useEffect(() => {
    if (!loaded && !hasError) return
    const t = window.setTimeout(advance, durationMs)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, hasError, durationMs])

  useEffect(() => {
    const t = window.setTimeout(advance, durationMs + 30_000)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl, durationMs])

  if (hasError) return <ContentErrorScreen />

  return (
    <div className="absolute inset-0 bg-black">
      <iframe
        src={fullUrl}
        title="content"
        className="w-full h-full border-0 bg-black"
        sandbox={
          linkUrl
            ? 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox'
            : 'allow-scripts allow-forms allow-same-origin'
        }
        referrerPolicy="strict-origin-when-cross-origin"
        onLoad={() => {
          warmed.add(fullUrl)
          framingBlocked.delete(fullUrl)
          setLoaded(true)
          // A late load is proof the page was only slow. Clearing this puts
          // the page back on screen instead of leaving the error up over
          // content that arrived a moment later.
          setHasError(false)
        }}
        onError={() => {
          if (linkUrl) {
            // A real load error from the element, not a guess from a clock.
            fail(`External link failed to load: ${linkUrl}`, true)
          } else {
            setHasError(true)
            setLoaded(true)
            onError(`Iframe failed to load: ${fullUrl}`)
          }
        }}
      />
      {!loaded && (
        <div className="absolute inset-0 bg-bg flex items-center justify-center">
          <div className="w-10 h-10 rounded-full border-[3px] border-brand border-t-transparent animate-spin" />
        </div>
      )}
    </div>
  )
}
